import {
  array,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';
import { AlbumItemSchema, ArtistItem, SortSchema } from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

const DEFAULT_MAX_ARTIST_ALBUMS = Number(
  import.meta.env.VITE_DEFAULT_MAX_ARTIST_ALBUMS,
);

export const LibraryArtistRequestArgsSchema = pipe(
  object({
    id: string(),
    limit: optional(number()),
    offset: optional(number()),
    sort: optional(SortSchema),
  }),
  transform(({ id, limit, offset, sort }) => {
    return {
      id,
      params: {
        ...(limit ? { limit } : { limit: DEFAULT_MAX_ARTIST_ALBUMS }),
        ...(offset ? { offset } : {}),
        ...(sort ? { sort } : {}),
      },
    };
  }),
);

export const LibraryArtistResponseSchema = pipe(
  object({
    data: array(AlbumItemSchema),
    meta: object({
      total: number(),
    }),
    next: optional(string()),
  }),
  transform(({ data, meta, next }) => {
    const albumList = {
      albums: data,
      total: meta.total,
      next: next === undefined ? undefined : getOffsetNumber(next),
    };

    return {
      artist: {
        id: '',
        name: data[0].artistName,
        type: 'artist' as const,
        artwork: undefined,
      } as ArtistItem,
      albumList,
    };
  }),
);

export const MeLibraryArtistWithAlbumsAPISchema = object({
  data: LibraryArtistResponseSchema,
});

export type LibraryArtistRequestArgsInput = InferInput<
  typeof LibraryArtistRequestArgsSchema
>;

export type LibraryArtistRequestArgs = InferOutput<
  typeof LibraryArtistRequestArgsSchema
>;

export type LibraryArtistResponse = InferOutput<
  typeof LibraryArtistResponseSchema
>;

export type MeLibraryArtistWithAlbumsAPI = InferOutput<
  typeof MeLibraryArtistWithAlbumsAPISchema
>;

export const $getMeLibraryArtistWithAlbums = async (
  player: MusicKit.MusicKitInstance,
  args: LibraryArtistRequestArgsInput,
): Promise<LibraryArtistResponse> => {
  const p = safeParse(
    LibraryArtistRequestArgsSchema,
    args,
  );
  if (!p.success) {
    throw new ValiError(p.issues);
  }

  const { id, params } = p.output;
  const res = await player.api.music(
    `/v1/me/library/artists/${id}/albums`,
    params,
  ) as unknown as InferInput<typeof MeLibraryArtistWithAlbumsAPISchema>;
  const parsed = safeParse(
    MeLibraryArtistWithAlbumsAPISchema,
    res,
  );
  if (!parsed.success) {
    throw new ValiError(parsed.issues);
  }
  const response = parsed.output.data;
  response.artist.id = id;
  return response;
};
